import {
  ConsoleLogger,
  type HttpAugments,
  type RequestOptions,
  StorageService,
  request,
} from '@nextgen-web-framework/core';
import { cloneDeep } from 'lodash';
import { profileApi, ContactType } from '@commerce-profile-webcomponents/sdk';
import { Address, CheckoutProfileService } from '@commerce-checkout-webcomponents/sdk';
import {
  Cart,
  CartEntryDetails,
  ErrorResponse,
  Severity,
  cartApi,
  OrderType,
  AmwayUserDetails,
  UpdateCartRequest,
  ExceptionsEnum,
  AccountProcessType,
  SubCartDetail,
  DeliveryType,
  BundleSlot,
} from '../services';
import { ApiProjections, CartConstantsEnum, CartEventsEnum, CartReqOtherOptions, SWITCH_PROXY_TYPE } from '../types';
import { CartResponse } from '../services/models/CartResponse';
import { Exceptions } from '../services/models/Exceptions';
import { GetCartsResponse } from '../types/cartTypes';
import { AccountProductType } from '../services/models/AccountProductType';
import { CartEvents } from './cartEventsUtil';
import { getAllEntries } from './cartLineItemsHelper';
export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
export const formatPromoDate = (date: string | Date) => {
  const dateAtt = new Date(date);
  let dateStr = '';
  if (new Date(dateAtt).setHours(0, 0, 0, 0) == new Date().setHours(0, 0, 0, 0)) {
    return true;
  }
  dateStr += monthNames[dateAtt.getMonth()];
  dateStr += ' ';
  dateStr += String(dateAtt.getDate());
  dateStr += ', ';
  dateStr += String(dateAtt.getFullYear());
  return dateStr;
};

export enum ExceptionCode {
  BAD_REQUEST = '400 BAD_REQUEST',
}

export const CART_TYPE = {
  GROUP: 'group',
  PERSONAL: 'personal',
};

export enum CartRoutePaths {
  CART = 'cart',
  LIST = 'list',
  EXCEPTION = '404',
  HOME = '/',
  REGISTRATION_CHECKOUT = '/signup?returnUri=/checkout&flowName=shareBarFlow',
  SELF_LED_REGISTRAION = '/signup?flowName=selfInitiatedFlow&returnUri=/cart',
  MYSHOP = 'myshop',
}

export enum StockStatusEnum {
  TNA = 'OUT_OF_STOCK',
  NLA = 'NLA',
  ship = 'ship',
  BO = 'B/O',
}

export const isExceptionExist = (
  exceptionName: Exceptions | DeliveryType,
  errors: ErrorResponse[],
  getException = false,
) => {
  const exception = errors?.find(({ title, code }) => title === exceptionName || code === exceptionName);
  return getException ? exception : Boolean(exception);
};

export const getAllExceptionWithSeverity = (
  exceptionNames: Exceptions[],
  exceptionSeverity: Severity,
  errors: ErrorResponse[],
) =>
  errors?.filter(
    ({ severity, title }) => severity === exceptionSeverity && exceptionNames.includes(title as Exceptions),
  );

export const isMainCart = (currentCartId?: string) => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const SubCartID = currentCartId || StorageService.getLocalStorage().retrieve(CartConstantsEnum.SubCartID);
    return StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId) === SubCartID;
  }
  return false;
};

export const getActiveCart = (cart: Cart, queryParamCartId = '') => {
  const toSearchId = queryParamCartId ? queryParamCartId : cart.id;
  if (cart.orderType === OrderType.Gco) {
    return cart?.subCarts?.find((subCart) => subCart.subCartId === toSearchId) ?? cart?.subCarts?.[0];
  }

  return cart?.subCarts?.[0];
};

export const getErrorFromEntryOrSubCart = (data: SubCartDetail | CartEntryDetails) =>
  data.errors?.find((error) => error.params?.checkoutInventoryError === 'QUANTITY_UPDATED');

export const filterPromotionItemFromEntries = (entries: CartEntryDetails[]) =>
  entries.filter((entry) => entry.isPersistent);

export type ErrorAPI = {
  responseBody: ErrorResponse;
  message: string;
  responseBodyArg: ErrorResponse;
};

//Need to remove these from sdk and need to pick from backend enum
export const versionConflict = 'ENTITY_VERSION_CONFLICT';
export const CART_NOT_FOUND = 'CART_NOT_FOUND';
export const CART_ENTRY_NOT_FOUND = 'CART_ENTRY_NOT_FOUND';
export const UNAUTHORIZED = 'unauthorized';

export const publishLoaderEvent = (show = true, entryId?: number) =>
  CartEvents.publishEvent({
    event: show ? CartEventsEnum.ShowLoader : CartEventsEnum.HideLoader,
    payload: {
      cartId: '',
      entryId,
    },
  });

export const isGroupOrder = (orderType: string) => orderType === OrderType.Gco;
export const isProxyOrder = (orderType: string) => orderType === OrderType.Proxy;
export const isRegularOrder = (orderType: string) => orderType === OrderType.Regular;

export const createEmptyCart = async (skipLSUpdate = false) => {
  const options: CartReqOtherOptions = skipLSUpdate
    ? {
        skipCartIdStoreInLS: true,
        skipCartResStoreInLS: true,
        skipVersionStoreInLS: true,
        skipIsGroupCartStoreInLS: true,
      }
    : {};
  const payload = {
    orderType: OrderType.Regular,
    subCarts: [
      {
        entries: [],
      },
    ],
  };
  const res = (await cartApi.createCart({
    payload,
    options,
  })) as Cart;
  if (!skipLSUpdate) {
    StorageService.getLocalStorage().store(CartConstantsEnum.CartId, res.id);
    StorageService.getLocalStorage().store(CartConstantsEnum.CartReqVersion, res.version);
  }

  return res;
};

export const switchCart = async (
  cartData: Cart,
  orderType: OrderType,
  selectedProfile: AmwayUserDetails,
): Promise<Cart> => {
  let cart = cartData;
  const cartIdFromLS = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
  let mainCartId = cartIdFromLS;
  if (!cartIdFromLS) {
    cart = await createOrFetchCart();
    mainCartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
  }
  const accountId = selectedProfile?.accountId;
  const amwayUser = {
    amwayUserId: selectedProfile?.amwayUserId,
    accountId: selectedProfile?.accountId,
  };

  //Get all sub-carts
  const res = await cartApi.getSubCart({
    cartId: mainCartId,
    subCartId: mainCartId,
    projections: ApiProjections.FULL,
    options: {
      skipCartResStoreInLS: true,
      skipCartIdStoreInLS: true,
      skipVersionStoreInLS: true,
    },
  });

  const { subCarts } = res ?? {
    subCarts: [],
  };
  mainCartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
  const cartReqVersion = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion);

  //Check if the cart already exist for the selected account
  const cartProxy = subCarts.find(({ amwayUser }) => accountId === amwayUser?.accountId);
  let currentEntries: CartEntryDetails[] = [];

  if (cart.orderType === OrderType.Gco) {
    currentEntries = cart.subCarts.find(({ subCartId }) => cart.id === subCartId)?.entries as CartEntryDetails[];
  } else {
    currentEntries = cart.subCarts?.[0]?.entries as CartEntryDetails[];
  }

  const { withoutSOPEntry } = getAllEntries(currentEntries);

  if (cartProxy) {
    const switchedCart = (await cartApi.updateGCOFlag({
      cartId: mainCartId,
      subCartId: cartProxy.subCartId,
      payload: {
        orderType,
        amwayUser,
        version: +cartReqVersion,
      },
      options: {
        skipCartIdStoreInLS: true,
        skipCartResStoreInLS: true,
        skipVersionStoreInLS: false,
      },
    })) as Cart;

    if (!switchedCart.subCarts?.[0]?.entries?.length) {
      const payload = {
        subCarts: [
          {
            subCartId: switchedCart.subCarts?.[0].subCartId,
            entries: validateBundleInEntries(withoutSOPEntry),
          },
        ],
        orderType: OrderType.Proxy,
        version: switchedCart.version,
      };
      return cartApi.addToCart({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        subCartId: cart.id,
        projections: ApiProjections.DETAILED,
        payload,
      }) as Promise<Cart>;
    }

    return switchedCart;
  } else {
    //call create cart with selected product
    return cartApi.createSubCart({
      cartId: mainCartId,
      payload: {
        amwayUser,
        entries: validateBundleInEntries(withoutSOPEntry),
        orderType,
        version: +cartReqVersion,
      },
    }) as Promise<Cart>;
  }
};

export const switchToRegularCart = async (cart: Cart, cartOverrideFlag = false) => {
  const payload = {
    amwayUser: {
      amwayUserId: cart.amwayUser?.amwayUserId,
      accountId: cart.amwayUser?.accountId,
    },
    orderType: OrderType.Regular,
    version: cart.version,
  };
  const previousEntries = cart.subCarts?.[0]?.entries as CartEntryDetails[];
  const filteredEntries = getFilteredEntries(previousEntries || [], {
    filterPromotion: false,
    filterSOP: true,
    filterIsAccountFeeProduct: true,
    filterIsAccountKitProduct: true,
    filterUnsellableProduct: false,
    filterAuxiliaryProducts: true,
  });
  //const { withoutSOPEntry } = getAllEntries(previousEntries);
  const previousOrderType = cart.orderType;
  const mainCart = (await cartApi.updateGCOFlag({
    payload,
    cartId: cart.id,
    subCartId: cart.id,
    options: {
      skipCartIdStoreInLS: true,
      skipCartResStoreInLS: true,
      skipVersionStoreInLS: false,
    },
  })) as Cart;
  const entriesConditionCheck = cartOverrideFlag ? true : !mainCart.subCarts?.[0]?.entries?.length;

  if (entriesConditionCheck && previousOrderType === OrderType.Proxy) {
    const res =
      cartOverrideFlag &&
      (await cartApi.clearCartEntries({
        cartId: cart.id,
        subCartId: mainCart.id,
      }));

    const payload = {
      subCarts: [
        {
          subCartId: mainCart.id,
          entries: validateBundleInEntries(filteredEntries),
        },
      ],
      orderType: OrderType.Regular,
      version: cartOverrideFlag ? (res as Cart).version : mainCart.version,
    };

    return cartApi.addToCart({
      cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
      subCartId: cart.id,
      projections: ApiProjections.DETAILED,
      payload,
    });
  }
  return mainCart;
};

export const filterOutRegistrationProducts = (entries: CartEntryDetails[]) =>
  entries.filter(
    (entry) =>
      !includesAny(entry, [
        AccountProductType.Fee,
        AccountProductType.LateFee,
        AccountProductType.Ekit,
        AccountProductType.Pkit,
      ]),
  );

export const validateBundleInEntries = (cartEntries: CartEntryDetails[] = []) =>
  cartEntries.map((entry) => {
    if (entry?.bundleSlots?.length) {
      const clonedEntry = cloneDeep(entry);

      clonedEntry.bundleSlots?.forEach((slot) => {
        slot.entries.forEach((slotEntry) => {
          slotEntry.quantity = slotEntry.baseQuantity ?? slotEntry.quantity;
        });
      });
      return clonedEntry;
    }
    return entry;
  });

export const requestWithVersionFix = (requestOptions: RequestOptions, augments?: HttpAugments): Promise<CartResponse> =>
  request<Cart>(requestOptions, augments).catch(async (e: ErrorAPI) => {
    if (e.responseBody?.title === Exceptions.EntityVersionConflict) {
      const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
      const latestCart = (await cartApi.getSubCart({
        cartId,
        subCartId: cartId,
        projections: ApiProjections.MIN,
      })) as Cart;
      StorageService.getLocalStorage().store(CartConstantsEnum.CartReqVersion, latestCart.version);
      (requestOptions.body as unknown as { version: number }).version = latestCart.version;
      return request<Cart>(requestOptions, augments);
    }

    if (e.responseBody?.title === CART_NOT_FOUND) {
      const currentRequestionOption = cloneDeep(requestOptions);
      const currentCartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
      const currentRequestBody = currentRequestionOption.body as unknown as {
        subCarts: SubCartDetail[];
      };
      const { id, version } = await createOrFetchCart();
      currentRequestionOption.url = currentRequestionOption.url.replaceAll(
        encodeURIComponent(currentCartId),
        encodeURIComponent(id),
      );

      if ((currentRequestionOption.body as unknown as { version?: number })?.version) {
        (currentRequestionOption.body as unknown as { version: number }).version = version;
      }

      if (currentRequestBody?.subCarts) {
        currentRequestBody.subCarts[0].subCartId = id;
      }
      return request<Cart>(currentRequestionOption, augments);
    }

    if (e.responseBody?.title === Exceptions.SubCartNotFound) {
      const { id } = await createOrFetchCart();
      const latestCart = await cartApi.getSubCart({
        cartId: id,
        subCartId: id,
        projections: ApiProjections.DETAILED,
      });

      return latestCart;
    }

    if (e.responseBody?.title === Exceptions.CartEntryNotFound) {
      const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
      const latestCart = await cartApi.getSubCart({
        cartId,
        subCartId: cartId,
        projections: ApiProjections.DETAILED,
      });

      return latestCart;
    }

    if (e.responseBody?.code === UNAUTHORIZED) {
      cleanUpCartLS();
      const latestCart = await createOrFetchCart();
      return latestCart;
    }
    throw e;
  });
/**
 * This method is used to get id of the current cart.
 * @returns cart id
 */
export const getCurrentCartId = async () => {
  const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
  // If cart id is present in the localstorage then return that cart id.
  if (cartId) {
    return cartId;
  }

  // If cart id is not present in the localstorage then create a new cart and then return new cart id.
  const cartRes = await createOrFetchCart();
  return cartRes.id;
};

/**
 * This method is used to create the cart and update the cart.
 *
 * @param updateCartPayload
 *
 * @returns Update Cart Response
 */
export const createAndUpdateCart = async (updateCartPayload: UpdateCartRequest, skipLSUpdate = false) => {
  const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
  // If cart id is present in the localstorage then update the same cart.
  if (cartId && !skipLSUpdate) {
    const updateCartRes = await updateCart(cartId, updateCartPayload);
    return updateCartRes;
  }

  // If cart id is not present in the localstorage then create a new cart and update new cart.
  const cartRes = await createEmptyCart(skipLSUpdate);
  if (cartRes.id) {
    const updateCartRes = await updateCart(cartRes.id, updateCartPayload, skipLSUpdate, cartRes.version);
    return updateCartRes;
  }

  return null;
};

const updateCart = async (
  cartId: string,
  updateCartPayload: UpdateCartRequest,
  skipLSUpdate = false,
  cartVer?: number,
) => {
  const subCartId = cartId || StorageService.getLocalStorage().retrieve(CartConstantsEnum.SubCartID);
  const version = cartVer ? cartVer : StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion);

  const options: CartReqOtherOptions = skipLSUpdate
    ? {
        skipCartIdStoreInLS: true,
        skipCartResStoreInLS: true,
        skipVersionStoreInLS: true,
        skipIsGroupCartStoreInLS: true,
      }
    : {};

  const udpateCartRes = await cartApi.updateCart(
    cartId,
    subCartId,
    {
      ...updateCartPayload,
      // Overriding the cart version here to make sure we are passing updated version
      version: +version,
    },
    options,
  );
  return udpateCartRes;
};

export const hasInvalidCoupon = (data: Cart) => {
  const error =
    data?.errors &&
    data.errors?.find((error: ErrorResponse) => error.title === ExceptionsEnum.Exceptions.InvalidCoupons);

  return error;
};

export const deletePaymentDetails = (subCart: SubCartDetail) => {
  const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
  const version = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion);
  if (subCart?.paymentDetails?.[0]?.paymentPlanId) {
    return cartApi.updateCart(cartId, subCart?.subCartId as string, {
      removePaymentDetails: true,
      version: +version,
    });
  }

  return Promise.resolve(undefined);
};

export const userNotRegistered = (cartData: Cart) => {
  const accountData = cartData?.accountProcess;

  return accountData && !accountData?.id && accountData?.type != AccountProcessType.Renewal;
};

export const allProductDetailsForEventPublish = (cart: CartResponse) => {
  const productBrands: String[] = [];
  const productNames: String[] = [];
  const productIds: String[] = [];
  const productPVs: String[] = [];
  const productBVs: String[] = [];
  const productUnitPrices: String[] = [];
  const productUnits: String[] = [];
  const productPromo: String[] = [];
  const productReferrer: String[] = [];

  cart?.subCarts?.[0].entries?.forEach((entry) => {
    productBrands.push(entry?.product.brand || '');
    productNames.push(entry?.product.name || '');
    productIds.push(entry?.product.id || '');
    productBVs.push(String(entry?.retailPrice?.amwayValue?.businessVolume));
    productPVs.push(String(entry?.retailPrice?.amwayValue?.pointValue));
    productUnitPrices.push(String(entry?.retailPrice?.value));
    productUnits.push(String(entry?.quantity));
    productPromo.push(String(entry?.promotionResults?.firedPromotions?.map((promo) => promo?.promotion?.promoCode)));
    productReferrer.push('');
  });

  return {
    productBrands,
    productNames,
    productIds,
    productPVs,
    productBVs,
    productUnitPrices,
    productUnits,
    productPromo,
    productReferrer,
  };
};

export const getFilteredEntries = (
  cartEntries: CartEntryDetails[],
  options: {
    filterSOP: boolean;
    filterPromotion: boolean;
    filterIsAccountFeeProduct: boolean;
    filterIsAccountKitProduct: boolean;
    filterUnsellableProduct?: boolean;
    filterAuxiliaryProducts?: boolean;
  },
) =>
  cartEntries.filter((entry) => {
    if (options.filterPromotion && entry?.partnerPromotionId) {
      return false;
    }

    if (options.filterSOP && entry?.sopElementId) {
      return false;
    }

    if (options.filterIsAccountFeeProduct && includesAny(entry, [AccountProductType.Fee, AccountProductType.LateFee])) {
      return false;
    }

    if (options.filterIsAccountKitProduct && includesAny(entry, [AccountProductType.Ekit, AccountProductType.Pkit])) {
      return false;
    }

    if (options.filterSOP && entry?.sopElementId) {
      return false;
    }

    if (options?.filterUnsellableProduct && entry.product.isSellable !== undefined && !entry.product.isSellable) {
      return false;
    }
    if (options?.filterAuxiliaryProducts && entry?.product?.isAuxiliary) {
      return false;
    }

    return true;
  });

export const isPVBVEarnedAndUpliftPresent = (
  upliftPV?: number | undefined,
  upliftBV?: number | undefined,
  earnedBV?: number | undefined,
  earnedPV?: number | undefined,
) => {
  const pvbvUpliftPresent = upliftPV || upliftBV;
  const pvbvEarnedPresent = earnedBV || earnedPV;
  return pvbvEarnedPresent && pvbvUpliftPresent;
};

export const updateShippingAddressInLS = async (
  ownerId: string,
  accountNumber: string,
  contactType: ContactType = ContactType.Shipping,
) => {
  try {
    const addressResponse = await profileApi.getShippingAddressList({
      contactType,
      ownerId,
      accountNumber,
    });
    //Find the primary address
    const primaryAddress = addressResponse.contacts.find(({ isDefault }) => isDefault);
    //Update the primary address in local storage using checkout function if primary address exist
    primaryAddress && CheckoutProfileService.updateDeliveryAddress(primaryAddress as Address, true);
    return primaryAddress ? addressResponse : null;
  } catch {
    return null;
  }
};

export const updateShippingAddressToEmpty = () => CheckoutProfileService.updateDeliveryAddress(undefined, true);

export const updateShippingAddress = async (amwayUserId: string, accountId: string, updateAddress?: () => void) => {
  const res = await updateShippingAddressInLS(amwayUserId, accountId);

  if (res === null || res?.contacts.length === 0) {
    updateShippingAddressToEmpty();
  }
  updateAddress?.();
};

export const switchOrOverrideCart = async (
  activeCart: SubCartDetail,
  amwayUser: AmwayUserDetails,
  switchType: SWITCH_PROXY_TYPE,
) => {
  let mainCartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);

  if (!mainCartId) {
    await createOrFetchCart();
    mainCartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
  }

  //Get all sub-carts
  const res = await cartApi.getSubCart({
    cartId: mainCartId,
    subCartId: mainCartId,
    projections: ApiProjections.FULL,
    options: {
      skipCartResStoreInLS: true,
      skipCartIdStoreInLS: true,
      skipVersionStoreInLS: true,
    },
  });

  const proxyCart = res.subCarts.find(
    (subCart) => subCart.amwayUser?.accountId === amwayUser.accountId,
  ) as SubCartDetail;
  mainCartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
  const switchedCart = (await cartApi.updateGCOFlag({
    cartId: mainCartId,
    subCartId: proxyCart?.subCartId,
    payload: {
      orderType: OrderType.Proxy,
      amwayUser,
      version: +res.version,
    },
    options: {
      skipCartIdStoreInLS: true,
      skipCartResStoreInLS: true,
      skipVersionStoreInLS: false,
    },
  })) as Cart;

  if (switchType === SWITCH_PROXY_TYPE.OVERRIDE) {
    const mainCartEntries = activeCart.entries?.map((entry) => ({
      entryId: entry.entryId as number,
      partnerPromotionId: entry.partnerPromotionId,
      sopElementId: entry.sopElementId,
      product: {
        id: entry.product.id,
        isAuxiliary: entry?.product?.isAuxiliary,
      },
      bundleSlots: entry.bundleSlots ?? [],
      quantity: entry.quantity,
    })) as CartEntryDetails[];

    //Entries for override cart
    const filteredEntries = getFilteredEntries(mainCartEntries || [], {
      filterPromotion: false,
      filterSOP: true,
      filterIsAccountFeeProduct: true,
      filterIsAccountKitProduct: true,
      filterUnsellableProduct: false,
      filterAuxiliaryProducts: true,
    });

    //Entries to be deleted
    const updatedEntries = proxyCart.entries?.map((entry) => ({
      entryId: entry.entryId,
      partnerPromotionId: entry.partnerPromotionId,
      product: {
        id: entry.product.id,
      },
      quantity: 0,
    })) as CartEntryDetails[];

    return cartApi.updateCartEntries({
      cartId: mainCartId,
      subCartId: proxyCart.subCartId,
      payload: {
        subCarts: [
          {
            subCartId: proxyCart.subCartId,
            entries: validateBundleInEntries(updatedEntries?.concat(filteredEntries)),
          },
        ],
        version: switchedCart.version,
      },
      options: {
        skipCartIdStoreInLS: true,
        skipCartResStoreInLS: true,
        skipVersionStoreInLS: false,
      },
    });
  }

  return switchedCart;
};

export const cleanUpCartLS = () => {
  const { CartId, CartReqVersion, IsGroupCart, SubCartID } = CartConstantsEnum;
  [CartId, CartReqVersion, IsGroupCart, SubCartID].forEach((key) => StorageService.getLocalStorage().remove(key));
};

export const updatedSelectedStateSubCart = (subCart: SubCartDetail, checked: boolean) => {
  const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
  const version = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion);
  return cartApi.updateCart(cartId, subCart.subCartId as string, {
    selected: checked,
    version: Number(version),
    orderType: OrderType.Gco,
  });
};

export const updateCartProfileSwitch = async () => {
  const { session } = SessionService.getSession();
  cleanUpCartLS();
  const allCarts = (await cartApi.getCarts({
    projections: ApiProjections.MIN,
    options: {
      skipCartIdStoreInLS: true,
      skipCartResStoreInLS: true,
      skipIsGroupCartStoreInLS: true,
      skipVersionStoreInLS: true,
    },
  })) as GetCartsResponse;

  if (allCarts.carts.length) {
    const [latestCart] = allCarts.carts;
    const { id, version } = latestCart;
    const { store } = StorageService.getLocalStorage();
    store(CartConstantsEnum.CartId, id);
    store(CartConstantsEnum.CartReqVersion, version);
    updateShippingAddressInLS(latestCart.amwayUser.amwayUserId, latestCart.amwayUser.accountId as string);
    return;
  }
  updateShippingAddressInLS(session.id as string, session.aboNum as string);
};

export const createOrFetchCart = async () => {
  const localStorage = StorageService.getLocalStorage();
  const res = (await cartApi.getCarts({
    projections: ApiProjections.DETAILED,
    options: {
      skipCartIdStoreInLS: true,
      skipCartResStoreInLS: true,
      skipIsGroupCartStoreInLS: true,
      skipVersionStoreInLS: true,
    },
  })) as GetCartsResponse;
  if (res?.carts?.length) {
    const [latestCart] = res.carts;
    localStorage.store(CartConstantsEnum.CartId, latestCart.id);
    localStorage.store(CartConstantsEnum.CartReqVersion, latestCart.version);
    return latestCart;
  }
  return createEmptyCart();
};

export const publishEventHandler = (msg: string, isSuccess = false) =>
  CartEvents.publishEvent({
    event: isSuccess ? CartEventsEnum.RedeemSuccessful : CartEventsEnum.RedeemFailed,
    payload: {
      cartId: '',
    },
    message: msg,
  });

export const redeemAndAddToCart = async (productId: string, quantity: number, bundleSlots?: BundleSlot[]) => {
  try {
    const redeemRes = await cartApi.redeemAPoints({
      productId: productId,
    });
    let fetchCartRes: SubCartDetail = {};
    const cartIdInLS = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);

    if (redeemRes?.error?.title) {
      switch (redeemRes?.error?.title) {
        case Exceptions.RedeemAmpointInsufficient:
          return publishEventHandler('cart.cartCommonHelpers.redeemAmpointInsufficient');
        case Exceptions.RedeemAmpointMaxTotalQuotaExceed:
          return publishEventHandler('cart.cartCommonHelpers.redeemAmpointMonthlyQuota');
        case Exceptions.RedeemAmpointMaxPerAccountQuotaExceed:
          return publishEventHandler('cart.cartCommonHelpers.redeemAmpointFullQuota');
        default:
          return publishEventHandler('cart.addToCartButton.exceptionOccured');
      }
    }

    if (!cartIdInLS) {
      const res = await createOrFetchCart();
      fetchCartRes = getActiveCart(res);
    }
    const payload = {
      subCarts: [
        {
          subCartId: fetchCartRes?.subCartId ?? cartIdInLS,
          entries: [
            {
              product: {
                id: productId,
              },
              quantity,
              bundleSlots,
            },
          ],
          couponCode: redeemRes.couponCode,
        },
      ],
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };

    try {
      await cartApi.addToCart({
        cartId: cartIdInLS,
        subCartId: cartIdInLS,
        projections: ApiProjections.DETAILED,
        payload,
      });
    } catch (e) {
      ConsoleLogger.log(e);
    }
  } catch (e) {
    return publishEventHandler('cart.addToCartButton.exceptionOccured');
  }

  return publishEventHandler('success', true);
};

export const isMainCartNotEmpty = (cart: CartResponse, cartId: string) => {
  const activeCart = getActiveCart(cart, cartId as string);
  //need to hide/show ellipses on main cart details page in gco mode and will always be shown on other subcarts
  const mainCartNotEmpty =
    isGroupOrder(cart.orderType) && (activeCart?.subCartId !== cart.id || !cartId)
      ? true
      : activeCart?.entries && activeCart?.entries?.length !== 0;

  return mainCartNotEmpty;
};

export const getAccountProductType = (cartEntries: CartEntryDetails[], productType: AccountProductType) =>
  cartEntries?.find((entry: CartEntryDetails) => entry?.accountProductTypes?.includes(productType));

export const includesAny = (entry: CartEntryDetails, types: AccountProductType[]) =>
  types.some((type) => entry?.accountProductTypes?.includes(type));

export const exceptionExist = (errorData: ErrorResponse[]) => {
  const allowedTitles = [
    DeliveryType.Bopis,
    DeliveryType.Homedelivery,
    DeliveryType.Kerry,
    DeliveryType.Seveneleven,
    ExceptionsEnum.Exceptions.Restricted,
    ExceptionsEnum.Exceptions.ProductOutOfStock,
    ExceptionsEnum.Exceptions.RationingLimitExceed,
    ExceptionsEnum.Exceptions.SopElementExpired,
  ] as string[];

  const exception = errorData?.find(
    ({ title, code }) => (!!title && allowedTitles.includes(title)) || (!!code && allowedTitles.includes(code)),
  );

  return exception;
};
